<script setup lang="ts">
import { computed } from 'vue'
import {
  useSkyBreakpoint,
  SkyImage,
  SkyProductTile,
  SkyCard,
  SkyCarousel,
  SkyLink,
  SkyButton,
} from '@groveco/skylight'
import { useMediaQuery } from '@vueuse/core'

import {
  FALLBACK_PRODUCT_IMAGE,
  PRODUCT_TILE_TRANSFORMATIONS,
  DEFAULT_TRANSFORMATIONS,
} from '~/constants/images'
import type { ContentfulAsset } from '~/types/contentful'

import { BACK_SOON_BADGE_TEXT } from '~/constants/productBadges'

/**
 * Component for ProductCarousel widgets.
 *
 * Displays the provided catalog items in a product tile carousel and
 * according to the provided props.
 */
const props = defineProps({
  /**
   * The category of products to display in the carousel.
   */
  category: {
    type: String,
    required: true,
  },
  /**
   * Copy for widget call to action.
   */
  ctaText: {
    type: String,
    default: '',
  },
  /**
   * Destination of widget call to action.
   */
  ctaDestinationUrl: {
    type: String,
    default: '',
  },
  /**
   * The widget image to display at medium+ viewport sizes, given `isThemed` truthy.
   */
  desktopImage: {
    type: Object as PropType<ContentfulAsset>,
    default: null,
  },
  /**
   * Alt description for widget image at medium+ viewport sizes, given
   * `isThemed` truthy. If null, the widget falls back to the description on
   * the asset.
   */
  desktopAltText: {
    type: String,
    default: null,
  },
  /**
   * Whether or not the add to cart CTA should be displayed in product tiles.
   */
  displayTileCta: {
    type: Boolean,
    default: false,
  },
  /**
   * Heading text for widget.
   */
  heading: {
    type: String,
    default: '',
  },
  /**
   * Whether to display a carousel in a themed layout with an accompanying
   * image/illustration.
   */
  isThemed: {
    type: Boolean,
    default: false,
  },
  /**
   * The image to display at small/mobile viewport sizes, given `isThemed` truthy.
   */
  mobileImage: {
    type: Object as PropType<ContentfulAsset>,
    default: null,
  },
  /**
   * Alt description for widget image at small/mobile viewport sizes, given
   * `isThemed` truthy. If null, the widget falls back to the description on
   * the asset.
   */
  mobileImageAltText: {
    type: String,
    default: null,
  },
  /**
   * The subheading text for the widget.
   */
  subheading: {
    type: String,
    default: '',
  },
})

const { isSmallUp, isMediumUp, isLargeUp } = useSkyBreakpoint()
// Resolution between isSmallUp and isMediumUp that doesn't match our Skylight breakpoints
const isTablet = useMediaQuery('(min-width: 800px) and (max-width: 1080px)')

const { fetchRecommendations, results: catalogItems } =
  useCatalogRecommendations()

const sectionClasses = computed(() => ({
  WidgetProductCarousel: true,
  'WidgetProductCarousel--themed': props.isThemed,
}))

const tileClasses = computed(() => ({
  WidgetProductCarousel_Tile: true,
  'WidgetProductCarousel_Tile--no-cta': !props.displayTileCta,
}))

const numItemsToDisplay = computed(() => {
  if (isTablet.value) {
    return 3
  }

  if (props.isThemed && isMediumUp.value) {
    return 4
  }

  if (isLargeUp.value) {
    return 6
  }

  if (isMediumUp.value) {
    return 5
  }

  return 2.5
})

const showNav = computed(() =>
  isMediumUp.value ? catalogItems.value.length > numItemsToDisplay.value : false
)

onBeforeMount(() => {
  fetchRecommendations('browse', {
    category: props.category,
  })
})
</script>

<template>
  <section :class="sectionClasses" data-test-id="carousel_container">
    <div
      v-if="isThemed"
      class="WidgetProductCarousel_Image"
      data-test-id="carousel_image"
    >
      <SkyImage
        v-if="mobileImage.url && !isMediumUp"
        :src="
          transformImage(mobileImage.url, `${DEFAULT_TRANSFORMATIONS},w_560`)
        "
        :alt="mobileImageAltText || mobileImage.description"
      />
      <SkyImage
        v-else-if="desktopImage.url && isMediumUp"
        :src="
          transformImage(desktopImage.url, `${DEFAULT_TRANSFORMATIONS},w_736`)
        "
        :alt="desktopAltText || desktopImage.description"
      />
    </div>

    <div class="WidgetProductCarousel_Headings">
      <h2 v-if="heading" class="WidgetProductCarousel_Headings_Title">
        {{ heading }}
      </h2>
      <p
        v-if="subheading"
        class="WidgetProductCarousel_Headings_Subtitle"
        data-test-id="carousel_subtitle"
      >
        {{ subheading }}
      </p>
    </div>

    <SkyCarousel
      v-if="catalogItems.length"
      class="WidgetProductCarousel_Tiles"
      :navigation="showNav"
      :slides-to-show="numItemsToDisplay"
      :slides-to-scroll="Math.round(numItemsToDisplay - 1)"
    >
      <SkyCard
        v-for="(item, index) in catalogItems"
        :key="item.variantId"
        :class="tileClasses"
        :aria-label="
          numItemsToDisplay > 1 &&
          `slide ${index + 1} of ${catalogItems.length}`
        "
        aria-roledescription="slide"
        role="group"
        rounded
        :data-cnstrc-item-price="item.price"
        :data-cnstrc-item-id="item.variantId"
        :data-cnstrc-item-name="item.name"
        :data-cnstrc-item-variation-id="item.variantId"
      >
        <SkyProductTile
          :badge-inverted="item.badgeText === BACK_SOON_BADGE_TEXT"
          :badge-text="item.badgeText"
          :brand-name="item.brandName"
          :image-alt="item.name"
          :image-placeholder="FALLBACK_PRODUCT_IMAGE"
          :image-src="
            transformImage(
              item.imageSrc,
              `${PRODUCT_TILE_TRANSFORMATIONS}${
                item.outOfStock ? ',e_grayscale' : ''
              }`
            )
          "
          :offer-price="item.price"
          :offer-text="displayTileCta ? item.discountText : null"
          :price="displayTileCta ? item.listPrice : null"
          :product-name="item.productName"
          :product-url="item.productUrl.toString()"
          :product-rating="item.reviewRating"
          :rating-count="item.reviewCount"
          :secondary-text="displayTileCta ? item.secondaryText : null"
          :variant-count="displayTileCta ? item.variants.length : 0"
          align-all-items
        >
          <template v-if="displayTileCta && item.variantId" #cta>
            <SkyButton v-if="item.outOfStock" disabled block>
              Out of Stock
            </SkyButton>
            <SkyLink
              v-else
              :to="`/statc/?v=${item.variantId}`"
              class="WidgetProductCarousel_ATC"
              button
            >
              Add to Cart
            </SkyLink>
          </template>

          <template #bottomBadge>
            <SkyImage
              v-if="item.beyondPlasticImg"
              :src="item.beyondPlasticImg"
              :alt="item.beyondPlasticAlt"
              :width="isSmallUp ? 64 : 52"
              data-test-id="bottom-badge"
            />
          </template>
        </SkyProductTile>
      </SkyCard>
    </SkyCarousel>
    <div
      v-else
      class="WidgetProductCarousel_Tiles WidgetProductCarousel_Tiles-loading SkyCarousel"
      aria-hidden="true"
    >
      <ol class="SkyCarousel_Slides">
        <li
          v-for="item in Math.round(numItemsToDisplay)"
          :key="`placeholder-${item}`"
          class="SkyCarousel_Slide"
        >
          <SkyCard :class="tileClasses" rounded>
            <SkyImage :src="FALLBACK_PRODUCT_IMAGE" />
          </SkyCard>
        </li>
      </ol>
    </div>
    <div
      v-if="ctaText && ctaDestinationUrl"
      class="WidgetProductCarousel_CTA"
      data-test-id="carousel_CTA"
    >
      <SkyLink :to="ctaDestinationUrl" inline>
        {{ ctaText }}
      </SkyLink>
    </div>
  </section>
</template>

<style lang="scss">
.WidgetProductCarousel {
  display: grid;
  row-gap: var(--spacing-4x);

  @include for-medium-up {
    row-gap: var(--spacing-8x);
  }

  &_Headings {
    &_Title {
      margin-left: var(--spacing-4x);
      text-wrap: wrap;

      @include type-brand-heading('2');

      @include for-medium-up {
        margin-left: 0;
        @include type-brand-display('2');
      }
    }

    &_Subtitle {
      @include type-body('m');
      margin-top: var(--spacing-2x);

      line-height: var(--line-height-tight);
      @include for-medium-up {
        @include type-body('l');
      }
    }
  }

  &_ATC {
    // Remove once we switch from SkyLink to SkyButton
    text-align: center;
    width: 100%;
    height: 100%;
    padding: var(--spacing-3x) var(--spacing-4x);
  }

  &_CTA {
    text-align: center;
    font-size: var(--font-size-75);

    @include for-medium-up {
      font-size: var(--font-size-100);
    }
  }

  &_Image {
    margin: 0 var(--spacing-4x);
  }

  &_Tiles {
    &-loading {
      --slide-width: 40%;
      @include for-medium-up {
        --slide-width: 20%;
      }
      .SkyCarousel_Slides {
        overflow: hidden;
      }
    }
  }

  .SkyCarousel_Slides {
    // Fixes odd Chrome only reflow issue where bottom 1px is sometimes lost.
    padding-bottom: 1px;
  }

  &_Tile {
    margin: 0 calc(var(--spacing-2x) * -1) 0 var(--spacing-4x);
    padding: var(--spacing-3x);
    overflow: hidden;

    @include for-medium-up {
      margin: 0 var(--spacing-1x);
    }

    // TODO(CS-529): Remove SkyProductTile_* override hacks.
    &--no-cta {
      --font-size-200: var(--font-size-100);

      .SkyProductTile_ImageContainer {
        margin-bottom: 0;
      }

      .SkyProductTile_Image {
        min-height: auto;
      }

      .SkyProductTile_VariantCount {
        display: none;
      }

      .SkyProductTile_Link {
        --font-size-100: var(--font-size-75);
        grid-template-rows: 1.5ch 3ch 0 2.2ch 1.6ch;
      }
    }
  }

  &--themed {
    @include for-medium-up {
      grid-template-columns: 0.7fr 1fr;
      grid-template-rows: auto 1fr auto;
      column-gap: var(--spacing-4x);

      .WidgetProductCarousel {
        &_Image {
          grid-row: 1;
          grid-column: 1;
          margin: 0;
        }

        &_Headings {
          margin-left: var(--spacing-6x);
          grid-row: 1;
          align-self: center;

          &_Subtitle {
            margin-top: var(--spacing-2x);
          }
        }

        &_Tiles {
          grid-row: 2;
          grid-column: 1 / span 2;
        }

        &_CTA {
          grid-column: 1 / span 2;
        }
      }
    }

    @include for-large-up {
      .WidgetProductCarousel {
        &_Image {
          grid-row: 1 / span 3;
          grid-column: 1;
        }

        &_Tiles {
          grid-column: 2;
        }

        &_CTA {
          grid-column: 2;
        }
      }
    }
  }
}
</style>
